import React from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import TemplateDetailTabs from '@components/shared/templates/TemplateDetailTabs';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Card, Descriptions, Button, Tag, Empty, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

// Import shared template components

interface ProductLabelTemplate {
  id: string;
  name: string;
  editable?: boolean;
  deletable?: boolean;
  paper_size_label?: string;
  is_default?: boolean;
  merchant?: { name?: string } | null;
  description?: string;
  created_at?: string;
  updated_at?: string;
  actual_paper_width?: number;
  actual_paper_height?: number;
  paper_width?: number;
  paper_height?: number;
  content?: string;
  [key: string]: unknown;
}

interface DeleteResponse {
  success: boolean;
  message?: string;
  [key: string]: unknown;
}

interface ProductLabelTemplatesShowProps {
  productLabelTemplate: ProductLabelTemplate | null;
  role?: 'admin' | 'operator';
  deleteTemplate: (id: string) => Promise<DeleteResponse>;
}

/**
 * ProductLabelTemplatesShow - Product Label Template detail view component
 */
const ProductLabelTemplatesShow: React.FC<ProductLabelTemplatesShowProps> = ({
  productLabelTemplate,
  role = 'admin',
  deleteTemplate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Sample product data from model (matching actual Product model attributes)
  const sampleProductData = {
    product: {
      sku: "SKU-001",
      name: "經典純棉T恤",
      barcode: "4712345678901",
      size: "M",
      color: "藍色",
      category: "服飾",
      spec: "100% 純棉，透氣舒適",
      price: 580.0,
      material: "100% 純棉",
      manufacturer_country: "台灣",
      hs_code: "6109.10.00",
      temperature: "ambient",
      note: "建議手洗或使用洗衣袋機洗，避免高溫烘乾。限量款式，售完為止。",
      created_at: "2025-01-03 10:30:00"
    },
    brand: {
      name: "經典品牌"
    },
    merchant: {
      name: "測試貨主",
      logo: { url: null }
    }
  };

  const handleEdit = () => {
    if (!productLabelTemplate) return;
    navigate(`/product_label_templates/${productLabelTemplate.id}/edit`);
  };

  const handleDelete = () => {
    if (!productLabelTemplate) return;
    Modal.confirm({
      title: t('common.confirm.delete_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm.delete_content', { name: productLabelTemplate.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteTemplate(productLabelTemplate.id);
          if (response.success) {
            message.success(t('common.success.deleted'));
            navigate('/product_label_templates');
          } else {
            message.error(response.message || t('common.error.operation_failed'));
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error(t('common.error.operation_failed'));
        }
      },
    });
  };

  if (!productLabelTemplate) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }

  // Calculate actual paper size and convert mm to px (1mm ≈ 3.7795px at 96 DPI)
  const MM_TO_PX = 3.7795;
  const actualWidth = productLabelTemplate.actual_paper_width || productLabelTemplate.paper_width;
  const actualHeight = productLabelTemplate.actual_paper_height || productLabelTemplate.paper_height;
  const widthPx = actualWidth ? actualWidth * MM_TO_PX : 378; // default 100mm
  const heightPx = actualHeight ? actualHeight * MM_TO_PX : 265; // default 70mm

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.label_templates', { label: productLabelTemplate.name }]}
        actions={
          <>
            {productLabelTemplate.editable && (
              <Can requiredPermission="products:write">
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                  {t('common.button.edit')}
                </Button>
              </Can>
            )}
            {productLabelTemplate.deletable && (
              <Can requiredPermission="products:write">
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  {t('common.button.delete')}
                </Button>
              </Can>
            )}
          </>
        }
      />

      <Card>
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('models.product_label_template.name')} span={2}>
            {productLabelTemplate.name || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.product_label_template.paper_size_label')}>
            {productLabelTemplate.paper_size_label || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('models.product_label_template.is_default')}>
            {productLabelTemplate.is_default ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t('shared.product_label_templates.list.is_default_true')}
              </Tag>
            ) : (
              <span style={{ color: '#999' }}>-</span>
            )}
          </Descriptions.Item>

          {role === 'operator' && productLabelTemplate.merchant && (
            <Descriptions.Item label={t('models.product_label_template.merchant_name')} span={2}>
              {productLabelTemplate.merchant.name || '-'}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t('models.product_label_template.description')} span={2}>
            {productLabelTemplate.description || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('common.created_at')}>
            {productLabelTemplate.created_at ? formatDateTime(productLabelTemplate.created_at) : '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('common.updated_at')}>
            {productLabelTemplate.updated_at ? formatDateTime(productLabelTemplate.updated_at) : '-'}
          </Descriptions.Item>
        </Descriptions>

        {/* Template Detail Tabs (Shared Component) */}
        <TemplateDetailTabs
          content={productLabelTemplate.content}
          sampleData={sampleProductData}
          paperSize={{
            width: actualWidth,
            height: actualHeight
          }}
          cssVariablePrefix="label"
          t={t}
          namespace="shared.product_label_templates"
          modelName="product_label_template"
        />
      </Card>
    </div>
  );
};

export default ProductLabelTemplatesShow;
